// PlusActionMenu — bündelt die früher 4 separat gerenderten Map-Action-
// Buttons (WAY ID, BLOCK, ONE-WAY, SCHLÜSSEL) sowie den neuen POI-markieren-
// Button hinter einem einzigen ⊕-Toggle.
//
// User-Wunsch (Wave 14cf-fix3):
//   "statt der 4 Logos mach ein schönes PLUS-Logo. Wenn ich es anklicke,
//    kommen die anderen als Option. Zusätzlich noch 'POI markieren'."
//
// Layout:
//   - Collapsed: nur das PLUS, klein, dezent.
//   - Expanded: PLUS oben + vertikaler Stack der verfügbaren Aktionen
//                darunter.
//   - Tap-outside / zweiter PLUS-Klick / nach erfolgreicher Aktion:
//                automatisch collapsen.
//
// Sichtbarkeitsregeln (vom Caller via Props gesteuert; Menu zeigt PLUS nur
// wenn mind. EINE Action sichtbar ist):
//   - WAY ID:        adminMode + discoveryMode + admin_light_plus
//   - BLOCK/ONE-WAY: zusätzlich `recording`-State + (gleiche Rolle)
//   - SCHLÜSSEL:     adminMode + recording + admin_light
//   - POI markieren: jeder eingeloggte User mit GPS-Fix
//
// Die einzelnen Action-Handler (snap, markWayOverride, markCruxSpot)
// leben weiterhin in DiscoveryButtons.tsx — wir embedden DiscoveryButtons
// direkt im expanded-Panel und ergänzen WAY ID + POI als eigene Buttons.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DiscoveryButtons } from './DiscoveryButtons';
import { positionBus } from '../lib/positionBus';
import { wayPickBus } from '../lib/wayPickBus';
import {
  USER_POI_CATEGORIES,
  markUserPoi,
} from '../lib/userPois';
import { postAdminPoi } from '../lib/adminPois';
import type { RiderRole } from '../lib/auth';
import { roleAtLeast } from '../lib/auth';

export interface PlusActionMenuProps {
  /** Eingeloggter User-Role. */
  role: RiderRole | null;
  /** Admin-Mode-Toggle aktiv (steuert Sichtbarkeit der Routing-Buttons). */
  adminMode: boolean;
  /** Discovery-Mode-Toggle aktiv. */
  discoveryMode: boolean;
  /** Recording aktiv (für BLOCK/ONE-WAY/SCHLÜSSEL). */
  recording: boolean;
  /**
   * Effektive Rolle die wir an DiscoveryButtons reichen — der Caller kann
   * z.B. „admin_light" simulieren wenn discoveryMode aus ist, sodass nur
   * SCHLÜSSEL aber nicht BLOCK/ONE-WAY gerendert wird (Logik aus MapPage
   * Wave 14ca-fix64 erhalten).
   */
  effectiveDiscoveryRole: RiderRole | null;
}

/** Verfügbarkeits-Helpers. Zentralisiert damit der „PLUS sichtbar?"-Check
 *  exakt dieselbe Logik nutzt wie das Rendern einzelner Buttons. */
function canShowWayId(props: PlusActionMenuProps): boolean {
  return (
    props.adminMode &&
    props.discoveryMode &&
    roleAtLeast(props.role, 'admin_light_plus')
  );
}
function canShowDiscoveryStack(props: PlusActionMenuProps): boolean {
  // DiscoveryButtons selbst rendert NIX bei nicht-admin_light. Wir gaten
  // hier zusätzlich auf Recording, weil das die alte Sichtbarkeitsregel
  // war (BLOCK/ONE-WAY/SCHLÜSSEL nur während Ride).
  return (
    props.recording &&
    props.adminMode &&
    roleAtLeast(props.role, 'admin_light') &&
    (props.discoveryMode ||
      !roleAtLeast(props.role, 'admin_light_plus'))
  );
}
function canShowPoiMark(_props: PlusActionMenuProps): boolean {
  // POI markieren ist für alle eingeloggten User verfügbar — der Bedarf
  // (Trinkwasser etc. taggen) ist Community-driven, nicht admin-gated.
  // GPS-Fix wird im Handler defensiv geprüft (sonst Fehler-Toast).
  return true;
}

export function PlusActionMenu(props: PlusActionMenuProps) {
  const showWay = canShowWayId(props);
  const showStack = canShowDiscoveryStack(props);
  const showPoi = canShowPoiMark(props);

  // Wenn keine einzige Action sichtbar ist: PLUS gar nicht rendern.
  // (Im aktuellen App-Stand passiert das nur wenn der User unauthentisiert
  // ist; POI-Mark ist sonst immer aktiv.)
  if (!showWay && !showStack && !showPoi) return null;

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [poiPickerOpen, setPoiPickerOpen] = useState(false);

  // Tap-outside-Close: window-Listener, schließt das Menu wenn der User
  // außerhalb von containerRef tippt. Modale (PoiCategoryPicker, OneWay-
  // Sheet) leben in Portals und sind damit AUSSERHALB von containerRef —
  // ein Klick darauf würde unser Menü schließen. Daher: solange ein Modal
  // offen ist, deaktivieren wir den Tap-Outside-Listener.
  useEffect(() => {
    if (!open || poiPickerOpen) return;
    const onPointerDownAnywhere = (e: PointerEvent) => {
      const el = containerRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDownAnywhere, true);
    return () => {
      window.removeEventListener('pointerdown', onPointerDownAnywhere, true);
    };
  }, [open, poiPickerOpen]);

  return (
    <div ref={containerRef} className="flex flex-col items-start gap-1.5">
      {/* PLUS-Button — kompakt + farbig (turbo-teal), klar als „Aktion"
          erkennbar. Beim Öffnen rotiert das Plus zu einem ✕ damit der
          User sofort sieht "tap again to close". */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-12 h-12 rounded-full bg-turbo-500 hover:bg-turbo-400 active:bg-turbo-600 text-white shadow-lg border border-white/20 flex items-center justify-center transition-all"
        aria-label={open ? 'Close actions' : 'Open actions'}
        aria-expanded={open}
      >
        <span
          aria-hidden="true"
          className="text-2xl leading-none font-bold inline-block transition-transform"
          style={{ transform: open ? 'rotate(45deg)' : 'none' }}
        >
          +
        </span>
      </button>

      {open && (
        <div className="flex flex-col items-start gap-1.5 mt-1">
          {showWay && (
            <button
              type="button"
              onClick={() => {
                wayPickBus.activate();
                setOpen(false);
              }}
              className="w-24 h-14 rounded-xl bg-sky-500/95 hover:bg-sky-400 active:bg-sky-600 text-white text-[13px] font-bold leading-none flex flex-col items-center justify-center gap-0.5 border border-sky-300/60 shadow-md transition-colors"
              title="Tap on the map to identify any OSM way and add it to overrides"
              aria-label="Activate way-pick mode"
            >
              <span aria-hidden="true" className="text-lg leading-none">🔍</span>
              <span className="tracking-wide">WAY ID</span>
            </button>
          )}
          {showStack && (
            <DiscoveryButtons role={props.effectiveDiscoveryRole} />
          )}
          {showPoi && (
            <button
              type="button"
              onClick={() => setPoiPickerOpen(true)}
              className="w-24 h-14 rounded-xl bg-teal-500/95 hover:bg-teal-400 active:bg-teal-600 text-white text-[13px] font-bold leading-none flex flex-col items-center justify-center gap-0.5 border border-teal-200/60 shadow-md transition-colors"
              title="POI an aktueller Position markieren"
              aria-label="POI markieren"
            >
              <span aria-hidden="true" className="text-lg leading-none">📍</span>
              <span className="tracking-wide leading-tight text-center">POI</span>
            </button>
          )}
        </div>
      )}

      {poiPickerOpen &&
        createPortal(
          <PoiCategoryPicker
            role={props.role}
            onClose={() => setPoiPickerOpen(false)}
            onMarked={() => {
              setPoiPickerOpen(false);
              setOpen(false);
            }}
          />,
          document.body,
        )}
    </div>
  );
}

// ─── PoiCategoryPicker ──────────────────────────────────────────────────
// Modal-Sheet zum Wählen einer POI-Kategorie.
//
// Wave 14cf-fix9: Rollen-aware Save-Branching.
//   - Admin / admin_light: POST zum Backend → alle User sehen den POI.
//                          Plus alle Kategorien sind aktiv (Café, Pranzo,
//                          etc. — der Admin soll die ganze Range markieren
//                          können).
//   - Regular User:        markUserPoi (lokale IDB) → nur dieser User sieht
//                          den POI. Aktive Kategorien wie in der USER_POI_
//                          CATEGORIES-Registry definiert (aktuell nur
//                          Trinkwasser).
//
// Nach erfolgreichem Save schließt das Modal sich + ruft onMarked() — der
// Caller (PlusActionMenu) collapsed dann das ganze PLUS-Menu.

interface PoiCategoryPickerProps {
  role: RiderRole | null;
  onClose: () => void;
  onMarked: () => void;
}

function PoiCategoryPicker({ role, onClose, onMarked }: PoiCategoryPickerProps) {
  // Wave 14cf-fix9: Admin-Mode = alle Kategorien aktiv. Regular User =
  // nur die in USER_POI_CATEGORIES als enabled markierten.
  const isAdminLevel = roleAtLeast(role, 'admin_light');
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const handlePick = async (categoryId: string) => {
    setBusy(categoryId);
    setFeedback(null);
    try {
      const pos = positionBus.get();
      if (!pos) {
        setFeedback('No GPS fix — cannot mark POI.');
        return;
      }
      const note = window.prompt('Short note (optional):', '');
      if (note === null) return;
      const ele =
        typeof pos.altitude === 'number' && Number.isFinite(pos.altitude)
          ? pos.altitude
          : null;

      if (isAdminLevel) {
        // Admin/admin_light: POST zum Server. postAdminPoi behandelt
        // Offline-Case (Queue) selbst und gibt einen optimistic POI zurück
        // damit der Map-Layer instant updated.
        await postAdminPoi({
          category: categoryId,
          lat: pos.latitude,
          lon: pos.longitude,
          ele,
          note: note.trim() || null,
        });
        // Map-Layer (siehe MapPage Custom-POI-useEffect) auf Refresh
        // triggern — sonst sieht der Admin seinen frischen POI erst nach
        // einem Reload.
        window.dispatchEvent(
          new CustomEvent('adt:poi-added', { detail: { kind: 'admin' } }),
        );
        setFeedback('✓ POI saved (visible to everyone)');
      } else {
        // Regular User: lokale IDB. Nur dieser User sieht den POI.
        await markUserPoi({
          lat: pos.latitude,
          lon: pos.longitude,
          category: categoryId,
          ele,
          note: note.trim() || null,
        });
        window.dispatchEvent(
          new CustomEvent('adt:poi-added', { detail: { kind: 'user' } }),
        );
        setFeedback('✓ POI saved (visible only to you)');
      }
      window.setTimeout(() => {
        onMarked();
      }, 800);
    } catch (err) {
      setFeedback(
        err instanceof Error ? `Error: ${err.message}` : 'Error while saving',
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/65 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Choose POI category"
      onClick={onClose}
    >
      <div
        className="bg-turbo-900 border border-turbo-500/40 rounded-2xl p-5 w-full max-w-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center mb-3">
          <h3 className="text-lg font-semibold text-white flex-1">
            Choose POI category
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full text-white/70 hover:text-white hover:bg-white/10 text-xl leading-none flex items-center justify-center"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <p className="text-[12px] text-white/60 mb-4 leading-snug">
          {isAdminLevel
            ? 'As an admin, your POI is immediately visible to ALL users. Pick a category:'
            : 'Marks a POI at your current GPS position. Visible only to you.'}
        </p>
        <div className="space-y-2">
          {USER_POI_CATEGORIES.map((cat) => {
            // Admins können alle Kategorien benutzen, normale User nur die
            // mit `enabled: true` in der Registry.
            const usable = isAdminLevel || cat.enabled;
            return (
            <button
              key={cat.id}
              type="button"
              disabled={!usable || busy !== null}
              onClick={() => handlePick(cat.id)}
              className={
                usable
                  ? 'w-full h-14 rounded-xl bg-teal-500/90 hover:bg-teal-400 active:bg-teal-600 text-white font-bold text-base flex items-center gap-3 px-4 border border-teal-300/40 shadow disabled:opacity-50 transition-colors'
                  : 'w-full h-14 rounded-xl bg-turbo-700/40 text-white/40 font-medium text-sm flex items-center gap-3 px-4 border border-white/5 cursor-not-allowed'
              }
              aria-disabled={!usable}
            >
              <span aria-hidden="true" className="text-2xl leading-none">
                {cat.emoji}
              </span>
              <span className="flex-1 text-left">
                {cat.label}
                {!usable && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-white/35">
                    soon
                  </span>
                )}
              </span>
              {usable && busy === cat.id && (
                <span className="text-[12px] font-normal opacity-80">…</span>
              )}
            </button>
            );
          })}
        </div>
        {feedback && (
          <div className="mt-4 text-[12px] text-white bg-turbo-700/60 px-3 py-2 rounded-lg leading-snug">
            {feedback}
          </div>
        )}
      </div>
    </div>
  );
}
